/**
 * This library used to lazy load ninja forms.
 *
 * The form content loaded by Ajax, dependencies needed to render the form composed in a way
 * to support a load of multiple forms on the page
 */
import { ResourceLoader } from '../utils/resource-loader';
import { isMaybeCloseToView, isInViewport } from '../utils/tools';
import { NFTPFormLoader, NFTPFormProcessor } from './contact-forms-lazy-load/nf-tp';
import { emailMeFormLoader, emailMeFormProcessor, maybeInitEmailMePopup, registerKeyBoardFocusHandler } from './contact-forms-lazy-load/email-me';

const evalExtraScriptData = function (script, redirectId, postId) {
	if (script.extra && script.extra.data) {
		// Indirect eval works in the global scope rather than the local scope
		// eslint-disable-next-line no-eval
		const geval = eval;
		const nfForms = null;
		const nfMPSettings = null;

		geval(script.extra.data);
		if (nfForms) {
			window.nfForms = window.nfForms.concat(nfForms);
		}

		if (redirectId) {
			window.nfForms[window.nfForms.length - 1].extra = { redirect_id: redirectId };
		}

		if (postId) {
			window.nfForms[window.nfForms.length - 1].extra = { post_id: postId };
		}
		window.nfFrontEnd = window.nfFrontEnd || nfFrontEnd;
		// needed to support multi-part forms
		window.nfMPSettings = window.nfMPSettings || nfMPSettings;
	}
};

const FORM_MODULE_SELECTOR = '.nf_tp_module, .et_pb_email_attorney';

const allFormModules = function () {
	return $(FORM_MODULE_SELECTOR);
};

// ENABLE_DIVI5 is a network-wide toggle, so it is on Divi 4 sites too; pair it with the
// per-site stylesheet class so only a site actually running Divi 5 starts an observer.
const isDivi5Builder = () => $('body.ENABLE_DIVI5.wp-child-theme-divi5_child').length > 0
	&& /[?&]et_fb=/.test(window.location.search);

const REQUESTED_FLAG = 'flNfFormRequested';

/**
 * A module with no form id yet is mid-edit: the author has cleared form_id in the shortcode
 * and has not typed the new one. The builder re-renders on every keystroke, so requesting a
 * form here would only fail. Leave it unclaimed instead and pick it up once an id arrives.
 *
 * Builder-only, so a genuinely misconfigured module on the front end still reports itself
 * rather than sitting there quietly.
 * AB#257953
 */
const isAwaitingFormId = function (element) {
	return isDivi5Builder() && !element.data('form_id');
};

/**
 * Form modules that have not been requested yet and are ready to be.
 */
const pendingFormModules = function () {
	return allFormModules().filter(function () {
		const element = $(this);
		return !element.data(REQUESTED_FLAG) && !isAwaitingFormId(element);
	});
};

/**
 * Last rendered element for each NF_TP module, kept so a re-render can reuse it.
 *
 * When the builder re-renders a module it swaps in an empty placeholder, but the element it
 * threw away is still in memory with the rendered form inside it. Moving those nodes across
 * keeps Ninja Forms' Backbone views bound to the DOM they were built against. Requesting the
 * form again cannot: it re-executes the form scripts, which redeclares their globals and
 * leaves the views pointing at elements that no longer exist ('An "el" undefined must exist
 * in DOM').
 * AB#257953
 */
const renderedModules = {};

/**
 * One loader for the whole document. A second batch - only the builder produces one - then
 * knows which scripts are already running and will not redeclare their globals.
 */
const resourceLoader = new ResourceLoader();

const hasFormContainer = function (formId) {
	return $(`#nf-form-${formId}-cont`).length > 0
		|| $(`[id^="nf-form-${formId}_"][id$="-cont"]`).length > 0;
};

/**
 * Drop nfForms entries whose container has been taken off the page - what happens when the
 * form chosen in Module Settings changes. Ninja Forms walks that list on start-up and throws
 * 'An "el" undefined must exist in DOM' on the first entry it cannot find a container for,
 * which stops it rendering the form that is actually there.
 * AB#257953
 */
const purgeDetachedForms = function () {
	if (!Array.isArray(window.nfForms)) {
		return;
	}
	window.nfForms = window.nfForms.filter(form => !form || !form.id || hasFormContainer(form.id));
};

const rememberRenderedModule = function (element) {
	const id = element.attr('id');
	if (!id) {
		return;
	}
	renderedModules[id] = { formId: String(element.data('form_id')), el: element.get(0) };
};

const reattachRenderedModule = function (element) {
	const node = element.get(0);
	const cached = renderedModules[element.attr('id')];
	if (!cached || cached.el === node || String(element.data('form_id')) !== cached.formId) {
		return false;
	}
	const rendered = Array.prototype.slice.call(cached.el.childNodes);
	if (rendered.length === 0) {
		return false;
	}
	rendered.forEach(child => node.appendChild(child));
	cached.el = node;
	return true;
};

/**
 * Get settled promises
 * TODO remove the es6-promise and replace this method with Promise.getSettled
 */
const getSettled = function(promises) {
	return promises.map(p => p.then((element) => {
		element.status = 'fulfilled';
		return element;
	}).catch((error) => {
		error.status = 'rejected';
		return error;
	}));
};

/**
 * A form whose request never settles leaves its "Loading Form..." preloader on the page
 * for good. Report the failure and drop the preloader so nothing keeps claiming to load.
 */
const reportFormFailure = function (error) {
	console.error('Contact form failed to load', error);
	const element = error && error.element;
	if (!element) {
		return;
	}
	if (element.hasClass('nf_tp_module')) {
		element.prev('span[id^=nf_tp_preloader_nf_tp_module_]').remove();
		return;
	}
	$('span[id=email_me_preloader]').remove();
};

/**
 * NF_TP and Email_Me forms lazy loading
 */
const loadAllForms = function () {
	// A module is claimed the moment it is requested rather than gating the whole function
	// once, so a second call - the viewport check and the scroll event can fire in the same
	// tick - still cannot request the same form twice, while a module added to the page later
	// is not locked out.
	const pendingModules = pendingFormModules();
	if (pendingModules.length === 0) {
		return;
	}

	const loadPromises = [];
	pendingModules.each(function () {
		const element = $(this);
		element.data(REQUESTED_FLAG, true);
		const formLoader = element.hasClass('nf_tp_module') ? NFTPFormLoader(element) : emailMeFormLoader(element);
		loadPromises.push(formLoader);
	});

	Promise.all(getSettled(loadPromises)).then((formsData) => {
		let nfLibraryScript = null;
		formsData.forEach((formData) => {
			if (formData.status === 'rejected') {
				reportFormFailure(formData);
				return;
			}
			if (formData.element.hasClass('nf_tp_module')) {
				NFTPFormProcessor(formData);
				rememberRenderedModule(formData.element);
			} else {
				emailMeFormProcessor(formData);
			}
			// since the last item is always the ninja-form
			// and there is a chance that we have two or more forms on a page
			// we need to load libraries needed by these forms BEFORE we load the main ninja-form file
			// so extracting this script from scripts of actual response
			// In the same time we should also remember about extra variables stored in this dependency
			nfLibraryScript = formData.response.scripts.pop();
			evalExtraScriptData(nfLibraryScript, formData.element.data('redirect_id'), formData.element.data('post_id'));
			nfLibraryScript.extra = null;
			formData.response.styles.forEach(stylesheet => resourceLoader.insertStylesheet(stylesheet.src));
		});
		formsData.forEach((formData) => {
			if (formData.status === 'rejected') {
				return;
			}
			formData.response.scripts.forEach((script) => {
				evalExtraScriptData(script);
				resourceLoader.scheduleScript(script.src);
			});
		});
		// Every form was rejected, so there is no ninja-forms library to pull in.
		if (!nfLibraryScript) {
			return;
		}
		// The Ninja Forms bundle has to run again to render a form added after start-up; its
		// dependencies must not, or they redeclare their globals and fail to parse.
		resourceLoader.setForceLoadedResources([nfLibraryScript.src]);
		if (isDivi5Builder()) {
			purgeDetachedForms();
		}
		resourceLoader.scheduleScript(nfLibraryScript.src);
		resourceLoader.loadScripts(() => {
			// We need to call resize after the form is rendered
			// to recalculate the parallax bg properties
			$(window).resize();
			document.dispatchEvent(new CustomEvent('all-nf-forms-loaded'));
		});
	}).catch(e => console.log(e));
};

const registerChangeFocusEvent = () => {
	$(document).on('nfFormReady', (e, layout) => {
		const layoutId = $(layout.$el[0]);
		const currentNFModule = $(layoutId).parent();
		let preloader = null;
		let formContainer = null;
		let removePreloader = true;
		if (currentNFModule.hasClass('nf_tp_module')) {
			// Check preloader for NF TP form
			preloader = currentNFModule.prev('span[id^=nf_tp_preloader_nf_tp_module_]');
			formContainer = currentNFModule;
		} else {
			// Check preloader for Email Me form
			preloader = $('span[id=email_me_preloader]');
			formContainer = $('#email_attorney_content');
		}

		//Check whether preloading text having keyboard focus
		if ($(preloader).is(':focus')) {
			const firstInput = $(formContainer).find('.nf-form-layout form input').first();
			if ($(firstInput).length === 0) {
				removePreloader = false;
			} else {
				$(firstInput).focus();
			}
		}
		if (removePreloader) {
			$(preloader).remove();
			if (document.getElementsByClassName('ENABLE_EMAIL_ME_CONTACT_FORM_POP_UP_FIX').length !== 0) {
				if ((formContainer[0].id) === 'email_attorney_content') {
					registerKeyBoardFocusHandler();
				}
			}
		}
	});
};

const allFormsFieldsUpdate = () => {
	$(document).on('nfFormReady', () => {
		//added aria hidden for * symbol
		$('.ninja-forms-req-symbol').each(function() {
			$(this).attr('aria-hidden', true);
		});
		//Handling the aria-invalid attribute according to the mandatory fields
		$('.nf-field-element input, .nf-field-element select, .nf-field-element textarea').each(function() {
			if (!$(this).prop('required')) {
				$(this).removeAttr('aria-invalid');
				$(this).removeAttr('aria-describedby');
			} else {
				$(this).attr('aria-invalid', false);
			}
			$(this).removeAttr('aria-labelledby');
		});
		//Append the aria-label for privacy policy
		$('.nf-field-element .form-links a').each(function() {
			const linkInaTag = $(this).attr('href');
			if (linkInaTag.indexOf('privacy') !== -1) {
				$(this).attr('aria-label', 'Privacy Policy (opens in a new tab)');
			}
		});
		//Append the aria-label for radio group
		$('.listradio-wrap ul').each(function() {
			$(this).attr('role', 'radiogroup');
			$(this).find('li label').css('display', 'inline-block');
		});
		//radio button attribute changes
		$('.listradio-wrap ul li input').each(function() {
			$(this).removeAttr('tabindex');
		});
		//Remove autocomplete attribute
		$('.nf-field-element textarea').each(function() {
			$(this).removeAttr('autocomplete');
		});
	});
};

//Apply autocomplete attribute according to the value
const FormsFieldsAutocompleteUpdate = (selector, labelFieldName, attributeValue) => {
	let index = 1;
	$(selector).each(function() {
		const currentLabelText = $(this).text().trim().toLowerCase();
		if ((currentLabelText.indexOf(labelFieldName)) !== -1) {
			const currentFieldText = $(this).closest('.nf-field-label').next('.nf-field-element').children('input');
			const autocompleteValue = labelFieldName === 'address' ? `${attributeValue}${index}` : attributeValue;
			index++;
			currentFieldText.attr('autocomplete', autocompleteValue);
		}
	});
};

const FormsFieldsScreenReaderFixes = () => {
	//Code for resolve Screen reader problem
	$('.listcheckbox-wrap .nf-field-label span').each(function() {
		const currentLabelId = $(this).attr('id');
		const checkboxMainDiv = $(this).closest('.nf-field-label').next('.nf-field-element');
		if (checkboxMainDiv.length) {
			checkboxMainDiv.attr('role', 'group');
			checkboxMainDiv.attr('aria-labelledby', currentLabelId);
			checkboxMainDiv.find('ul li input').attr('style', 'width: 1px !important');
			checkboxMainDiv.find('ul li label').css('display', 'inline-block');
		}
	});
};

const allFormsFieldsUpdateAutocomplete = () => {
	$(document).on('nfFormReady', () => {
		FormsFieldsAutocompleteUpdate('.nf-field-label label', 'address', 'address-line');
		FormsFieldsAutocompleteUpdate('.nf-field-label label', 'company', 'organization');
		FormsFieldsScreenReaderFixes();
	});
};

const appendPreloader = (nfFormContainer) => {
	const preloaderElement = $('<span tabindex="0" class="nf_preloader">Loading Form...</span>');
	if ($(nfFormContainer).hasClass('nf_tp_module')) {
		// Preloading text for every NF TP forms
		const NFTPModuleId = $(nfFormContainer).attr('id');
		const preloaderId = `nf_tp_preloader_${NFTPModuleId}`;
		if ($(`#${preloaderId}`).length > 0) {
			return;
		}
		preloaderElement.attr('id', preloaderId);
		$(nfFormContainer).before(preloaderElement);
		return;
	}
	if ($(nfFormContainer).hasClass('et_pb_email_attorney')) {
		// Preloading text for Email Me forms
		// It's not necessary to specify Email Me form ID since there is only one div container for Email Me forms loading
		const preloaderId = 'email_me_preloader';
		preloaderElement.attr('id', preloaderId);
		if ($(`#${preloaderId}`).length === 0) {
			$('#email_attorney_content').prepend(preloaderElement);
		}
	}
};

/**
 * The Divi 5 builder renders a module by POSTing its shortcode and grafting the markup into
 * the canvas, which happens well after document.ready. A one-shot pass never sees that
 * placeholder, so the module would sit empty for ever. Watch for late arrivals and run
 * another pass over whatever has not been requested yet.
 *
 * Builder-only: a front-end page has no late-injected form modules, and Divi 4 previews each
 * module in its own iframe that this bundle is not loaded into.
 * AB#257953
 */
const OBSERVED_MUTATIONS = { childList: true, subtree: true };

const holdsFormModule = function (node) {
	if (!node || node.nodeType !== Node.ELEMENT_NODE) {
		return false;
	}
	const candidate = $(node);
	return candidate.is(FORM_MODULE_SELECTOR) || candidate.find(FORM_MODULE_SELECTOR).length > 0;
};

const addedAFormModule = function (mutations) {
	return mutations.some(mutation =>
		Array.prototype.some.call(mutation.addedNodes, holdsFormModule));
};

const loadInjectedForms = function () {
	const pendingModules = pendingFormModules();
	if (pendingModules.length === 0) {
		return;
	}
	pendingModules.each(function () {
		const element = $(this);
		if (element.hasClass('nf_tp_module') && reattachRenderedModule(element)) {
			element.data(REQUESTED_FLAG, true);
			return;
		}
		appendPreloader(this);
	});
	// The canvas is not a scrolling front-end page, so load on sight.
	loadAllForms();
};

const watchForInjectedForms = () => {
	if (typeof MutationObserver === 'undefined') {
		return;
	}
	const observer = new MutationObserver((mutations) => {
		if (!addedAFormModule(mutations)) {
			return;
		}
		// An observer reports a batch once the DOM change is finished, so the markup is
		// complete here and there is nothing to wait for. Detaching over our own inserts keeps
		// them from queueing another batch; only synchronous work happens in between, so no
		// mutation from the builder can slip through the gap.
		observer.disconnect();
		try {
			loadInjectedForms();
		} finally {
			observer.observe(document.body, OBSERVED_MUTATIONS);
		}
	});
	observer.observe(document.body, OBSERVED_MUTATIONS);
};

const initAllForms = () => {
	const inViewPortHandlers = [];
	maybeInitEmailMePopup();

	allFormModules().each(function () {
		const showIfInViewPort = () => {
			if (isMaybeCloseToView(this, 2000) || isInViewport(this)) {
				inViewPortHandlers.forEach(handler => document.removeEventListener('scroll', handler));
				loadAllForms();
			}
		};
		inViewPortHandlers.push(showIfInViewPort);
		appendPreloader(this);
	});
	inViewPortHandlers.forEach((handler) => {
		document.addEventListener('scroll', handler);
		handler();
	});
	registerChangeFocusEvent();
	if (isDivi5Builder()) {
		watchForInjectedForms();
	}
	if (coreAccessibility.enable_nf_fields_update_a11y) {
		allFormsFieldsUpdate();
	}
	if (coreAccessibility.enable_nf_fields_update_autocomplete_a11y) {
		allFormsFieldsUpdateAutocomplete();
	}
};

const init = () => {
	/**
	 * nfForms it is a global variable that used by ninja-forms. This array contains all forms needed on the page.
	 */
	if (typeof window.nfForms === 'undefined') {
		window.nfForms = [];
	}
	if (typeof window.flNfFormsLoaded === 'undefined') {
		window.flNfFormsLoaded = {};
	}

	$(document).ready(() => {
		initAllForms();
	});
};

export default init;



// WEBPACK FOOTER //
// ./wp-content/plugins/core/assets/theme/js/src/forms/contact-forms-lazy-load.js